import React, { useEffect, useMemo, useState } from "react";
import { Alert, Table, Text } from "@mantine/core";
import { useServerApi } from "../hooks/userServerApi";

const BASE_CURRENCY_HKD = "HKD";

const PointExchangeRatePreview = ({ form }) => {
  const [api] = useServerApi();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectedCurrency = String(
    form?.values?.baseCurrency || ""
  ).toUpperCase();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await api.search({
          apiEntity: "exchangeRate",
          searchQuery: { active: true },
          pageSize: 1,
        });
        if (cancelled) return;
        setRates(result?.docs?.[0]?.rates ?? []);
      } catch (err) {
        if (!cancelled) {
          setRates([]);
          setError(err?.message ?? "Failed to load exchange rates");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const byCode = {};
    for (const row of rates) {
      const code = String(row?.code || "").trim().toUpperCase();
      const value = Number(row?.externalValue);
      if (!code || !Number.isFinite(value) || value <= 0) continue;
      byCode[code] = value;
    }
    if (byCode[BASE_CURRENCY_HKD] == null) {
      byCode[BASE_CURRENCY_HKD] = 1;
    }

    const codes = Object.keys(byCode);
    if (selectedCurrency && !byCode[selectedCurrency]) {
      codes.push(selectedCurrency);
    }
    codes.sort((a, b) => a.localeCompare(b));
    return codes.map((code) => ({
      code,
      externalValue: byCode[code],
    }));
  }, [rates, selectedCurrency]);

  if (loading) {
    return (
      <Text size="sm" color="dimmed">
        Loading exchange rates...
      </Text>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light">
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Text size="sm" weight={500} mb={4}>
        Active Exchange Rates (HKD per 1 unit)
      </Text>
      <Text size="xs" color="dimmed" mb="xs">
        Conversion uses these rates only when the order currency differs from
        the base currency.
      </Text>
      <Table fontSize="sm" withBorder>
        <thead>
          <tr>
            <th>Currency</th>
            <th>External Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = row.code === selectedCurrency;
            return (
              <tr
                key={row.code}
                style={{
                  background: selected ? "#e7f5ff" : undefined,
                  fontWeight: selected ? 600 : undefined,
                }}
              >
                <td>{row.code}</td>
                <td>
                  {row.externalValue == null
                    ? "Missing"
                    : row.externalValue}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

export default PointExchangeRatePreview;
